import type { MaterialLot, QualityBatchItem, QualityDocType } from '@entities/mto';
import type { BulkRow, BulkStatus } from './types';
import { NEW_LOT, SKIP_ROW } from './types';

/** Объект помечен прочерком, если не задан. */
export function siteOrDash(siteId: string | null): string {
  return siteId && siteId.trim() ? siteId : '—';
}

/** Количество с единицей в одну ячейку: `120 т`, либо прочерк. */
export function qtyWithUnit(quantity: string | null, unit: string | null): string {
  if (!quantity || !quantity.trim()) return '—';
  return unit ? `${quantity} ${unit}` : quantity;
}

/** Краткая подпись партии для select/ссылок: № партии · наименование. */
export function lotLabel(lot: MaterialLot): string {
  const head = lot.lot_no && lot.lot_no.trim() ? lot.lot_no : 'без №';
  return `${head} · ${lot.nomenclature}`;
}

/** Скачать blob-файл документа качества под его именем (как memo/passport). */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Ключ дедупликации новой партии в одном импорте: схлопнуть пробелы + lower. */
export function normKey(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Нормализовать тип к допустимому enum или пусто (модель могла вернуть null). */
export function toDocType(raw: string | null): '' | QualityDocType {
  return raw === 'passport' || raw === 'certificate' || raw === 'declaration' ? raw : '';
}

export function itemToRow(it: QualityBatchItem): BulkRow {
  const ex = it.extracted;
  // Паспорт безопасности и СГР для актов скрытых работ не нужны: строку
  // сразу пропускаем, снабженец может вернуть её выбором партии.
  const notQuality = ex.not_quality
    ? `Не документ о качестве (${ex.not_quality}): для актов скрытых работ не нужен, строка пропущена.`
    : null;
  return {
    key: `${it.index}-${it.part ?? 1}`,
    index: it.index,
    part: it.part ?? 1,
    parts: it.parts ?? 1,
    pages: it.pages ?? null,
    filename: it.filename,
    docType: toDocType(ex.doc_type),
    number: ex.number ?? '',
    docDate: ex.doc_date ?? '',
    issuer: ex.issuer ?? '',
    validUntil: ex.valid_until ?? '',
    nomenclature: ex.nomenclature ?? '',
    lotChoice: notQuality ? SKIP_ROW : it.suggested_lot ? it.suggested_lot.id : NEW_LOT,
    lotNo: ex.lot_no ?? '',
    matchedBy: it.suggested_lot?.matched_by ?? null,
    note: it.error ?? notQuality ?? ex.notes ?? null,
    status: 'pending',
    resultMsg: null,
  };
}

/** Строку можно импортировать: указан тип и разрешена партия (для новой — есть
 *  наименование). */
export function isImportable(r: BulkRow): boolean {
  if (r.lotChoice === SKIP_ROW || !r.docType) return false;
  if (r.lotChoice === NEW_LOT && !r.nomenclature.trim()) return false;
  return true;
}

export function skipReason(r: BulkRow): string {
  if (r.lotChoice === SKIP_ROW) return 'пропущен';
  if (!r.docType) return 'не указан тип';
  if (r.lotChoice === NEW_LOT && !r.nomenclature.trim()) return 'нет наименования для новой партии';
  return 'пропущен';
}

export function bulkStatusTone(s: BulkStatus): 'neutral' | 'success' | 'danger' | 'warning' {
  switch (s) {
    case 'done':
      return 'success';
    case 'error':
      return 'danger';
    case 'skip':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function bulkStatusLabel(r: BulkRow): string {
  switch (r.status) {
    case 'importing':
      return 'импорт…';
    case 'done':
      return r.resultMsg ?? 'готово';
    case 'error':
      return r.resultMsg ?? 'ошибка';
    case 'skip':
      return r.resultMsg ?? 'пропущен';
    default:
      return isImportable(r) ? 'к импорту' : skipReason(r);
  }
}
